import { BookmarkSimple, Flag, Heart, PaperPlaneTilt, Trash } from '@phosphor-icons/react';
import type { Comment, Post } from '@sirocco/shared';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { posts as postsApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/Bits';
import { PhotoViewer } from '../../components/ui/PhotoViewer';
import { PostText } from '../../components/ui/PostText';
import { readScreenCache, seedScreenCache, useAsync } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { horodatage, timeAgo } from '../../utils/format';
import { profilePath } from '../../utils/routes';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { PartageSheet } from '../../components/ui/PartageSheet';
import { Sheet, SheetAction } from '../../components/ui/Sheet';
import { reportPath } from '../report/ReportScreen';
import { lienDePost } from '../../utils/partage';
import { CommentComposer, type PoigneeComposeur } from './CommentComposer';
import { CommentRow } from './CommentRow';
import { t } from '../../i18n';

/**
 * Le fil d'une reponse : ce qu'elle a suscite.
 *
 * # Pourquoi un ecran, et non un decalage sous le commentaire
 *
 * Les reponses aux reponses tenaient d'abord dans le fil du post, a un
 * cran de decalage. Cela marche a trois reponses et se defait a trente :
 * le fil du post devient un mur ou l'on cherche a quoi repond quoi, et
 * la largeur qui reste au texte ne depend plus de ce qu'on lit mais de
 * la profondeur ou il se trouve.
 *
 * Un commentaire s'ouvre donc comme un post s'ouvre : on quitte la liste,
 * on voit une chose et ce qui lui repond, et l'on revient. Le fil du post
 * ne montre plus que les reponses de premier rang, chacune disant
 * combien elle en a suscite.
 *
 * # Tout le fil est lu, pas seulement cette branche
 *
 * `postsApi.comments` rend le fil entier du post : c'est une seule
 * lecture, deja en cache si l'on vient de l'ecran precedent, et le
 * decoupage se fait ici. Demander « les reponses de ce commentaire »
 * aurait exige un index de plus pour economiser quelques kilo-octets.
 */
export function CommentThreadScreen() {
  const { postId = '', commentId = '' } = useParams();
  const { state } = useLocation();
  const { user } = useSession();
  const composeur = useRef<PoigneeComposeur | null>(null);

  /*
    Le post d'ou vient tout cela, pose au-dessus et hors de vue.

    Meme chaine que dans le fil du post : la navigation, puis la memoire
    d'ecran, puis le reseau. La memoire sert l'ecran qu'on entrevoit
    derriere un geste de retour, qui remonte sans etat de navigation.
  */
  const [post, setPost] = useState<Post | null>(
    (state as { post?: Post } | null)?.post ?? readScreenCache<Post>(`post:${postId}`) ?? null,
  );
  const repereDeLaTete = useRef<HTMLDivElement>(null);
  /* La pile des parents : c'est sa taille qui bouge quand le reseau
     repond, donc c'est elle qu'on observe pour recaler. */
  const ligneeRef = useRef<HTMLDivElement>(null);
  /*
    La reponse ouverte, repeinte au doigt.

    Elle peut n'exister que dans l'etat de navigation — le fil n'est pas
    encore lu — donc aimer ou ranger ne peut pas se contenter de
    remplacer dans la liste : sans cette copie, le coeur ne bougerait pas
    tant que le reseau n'a pas repondu.
  */
  const [teteRepeinte, setTeteRepeinte] = useState<Comment | null>(null);
  /* La photo ouverte en plein ecran, s'il y en a une. */
  const [vue, setVue] = useState<string | null>(null);
  /* Le menu d'une reponse, et ce qu'il ouvre. */
  const ancreDuMenu = useRef<HTMLElement | null>(null);
  const [menu, setMenu] = useState<Comment | null>(null);
  const [partage, setPartage] = useState<{ postId: string; texte?: string } | null>(null);
  const { pending, request: ask, cancel, confirm } = useConfirm();
  const [toast, setToast] = useState<string | null>(null);
  const aller = useNavigate();
  const [envoi, setEnvoi] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);

  const { data, loading, error, reload, setData } = useAsync(
    () => postsApi.comments(postId),
    [postId],
    `comments:${postId}`,
  );

  /*
    Le post, quand il n'est pas venu avec la navigation. Une seule fois,
    et seulement s'il manque : l'ecran d'ou l'on vient l'a presque
    toujours sous la main.
  */
  useEffect(() => {
    if (post) {
      seedScreenCache(`post:${postId}`, post);
      return;
    }
    let vivant = true;
    void postsApi
      .byId(postId)
      .then((trouve) => {
        if (vivant && trouve) setPost(trouve);
      })
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, [post, postId]);

  /*
    On ouvre sur la reponse, le post pose au-dessus et hors de vue.

    Le post est ce dont la reponse parle : le mettre a portee de pouce
    evite d'avoir a revenir en arriere pour se rappeler de quoi il
    s'agissait. Mais ce n'est pas ce qu'on vient lire — on a appuye sur
    une reponse. Il est donc la, en glissant vers le bas, sans occuper
    l'ecran a l'arrivee.

    Sans animation : ce n'est pas un mouvement qu'on regarde, c'est la
    position de depart. La voir defiler donnerait l'impression que
    l'ecran s'est trompe et se rattrape.
  */

  /*
    Le commentaire ouvert, et tout ce qui en descend.

    L'ecran precedent le passe dans l'etat de navigation ; on le retrouve
    malgre tout dans la liste, qui est la seule source a jour — un like
    pose ici doit se voir, et l'objet de la navigation, lui, est fige a
    l'instant du depart.
  */
  const tete = data?.find((commentaire) => commentaire.id === commentId) ?? null;
  const passe = (state as { comment?: Comment } | null)?.comment ?? null;
  const ouvert = tete ?? teteRepeinte ?? passe;

  const enfants = new Map<string, Comment[]>();
  for (const commentaire of data ?? []) {
    if (!commentaire.parentId) continue;
    enfants.set(commentaire.parentId, [...(enfants.get(commentaire.parentId) ?? []), commentaire]);
  }
  /*
    Toute la lignee au-dessus, du post jusqu'au parent immediat.

    On ouvrait la reponse avec le seul post au-dessus, ce qui suffit au
    premier cran et ne suffit plus au troisieme : la reponse repondait a
    une reponse qu'on ne voyait nulle part, et il fallait remonter cran
    par cran pour comprendre de quoi il retournait. La chaine entiere est
    donc posee au-dessus, dans l'ordre ou elle a ete ecrite, chaque
    maillon relie au suivant par un trait.
  */
  const parId = new Map((data ?? []).map((commentaire) => [commentaire.id, commentaire]));
  const ancetres: Comment[] = [];
  {
    const vus = new Set<string>([commentId]);
    let curseur = ouvert?.parentId ?? null;
    while (curseur && !vus.has(curseur)) {
      const parent = parId.get(curseur);
      if (!parent) break;
      vus.add(curseur);
      ancetres.unshift(parent);
      curseur = parent.parentId;
    }
  }

  /*
    Seulement ce qui repond directement.

    Une reponse a une reponse a son propre fil, atteint d'un appui : la
    remonter ici ferait deux endroits ou la lire, et l'ecran cesserait
    d'etre le meme a chaque cran. La descente est sans fin et toujours
    identique — c'est ce qui la rend lisible.
  */
  const reponses = [...(enfants.get(commentId) ?? [])].sort((gauche, droite) =>
    gauche.createdAt.localeCompare(droite.createdAt),
  );

  async function envoyer(contenu: string, mentions: Parameters<typeof postsApi.comment>[3]) {
    setEnvoi(true);
    setEchec(null);
    try {
      const ajoute = await postsApi.comment(postId, contenu, commentId, mentions);
      setData((precedent) => [...precedent, ajoute]);
    } catch (cause) {
      setEchec(cause instanceof Error ? cause.message : t.fil.fil.envoiImpossible);
    } finally {
      setEnvoi(false);
    }
  }

  useLayoutEffect(() => {
    const bas = repereDeLaTete.current;
    const zone = bas?.closest('.scroll-area');
    const pile = ligneeRef.current;
    if (!bas || !(zone instanceof HTMLElement)) return;

    /*
      On ouvre sur la reponse, la lignee posee au-dessus et hors de vue.

      # Ce qui manquait, et qui n'etait pas une question de moment

      Amener la reponse tout en haut suppose qu'il y ait de quoi defiler
      **sous** elle. Une reponse sans reponse n'en avait pas : le
      defilement butait sur son maximum, et l'ecran s'ouvrait sur le haut
      de la lignee quoi qu'on fasse. Mesure au banc d'essai : `scrollTop`
      restait a zero alors qu'il fallait 563 px.

      D'ou `min-height: 100%` sur le bloc qui commence a la reponse : il
      fait au moins un ecran a lui seul, donc il peut toujours monter en
      haut. C'est la vraie correction ; le reste n'est que du calage.

      # Le calage, lui, attend que la lignee ait sa taille

      Le post et les parents arrivent du reseau et poussent le bloc vers
      le bas apres coup. On observe donc la pile de la lignee plutot que
      de reprendre la mesure a chaque image : c'est elle qui change, et
      un observateur de taille dit exactement quand.
    */
    let rendu = false;

    const caler = () => {
      if (rendu) return;
      const ecart = bas.getBoundingClientRect().top - zone.getBoundingClientRect().top;
      /* Le pixel d'ecart vient de l'arrondi : y toucher ferait vibrer. */
      if (Math.abs(ecart) > 1) zone.scrollTop += ecart;
    };

    /*
      Le premier geste rend la main.

      Sans cela, quelqu'un qui glisse vers la lignee pendant que le
      reseau repond se verrait ramene par un calage qui n'a pas encore
      lache prise, et l'ecran paraitrait se battre contre son doigt.
    */
    const rendreLaMain = () => {
      rendu = true;
      observateur?.disconnect();
      zone.removeEventListener('touchstart', rendreLaMain);
      zone.removeEventListener('wheel', rendreLaMain);
    };

    const observateur = pile ? new ResizeObserver(caler) : null;
    caler();
    if (pile) observateur?.observe(pile);
    zone.addEventListener('touchstart', rendreLaMain, { passive: true });
    zone.addEventListener('wheel', rendreLaMain, { passive: true });
    const fin = setTimeout(rendreLaMain, 1200);

    return () => {
      rendreLaMain();
      clearTimeout(fin);
    };
  }, [commentId]);

  /*
    On peint l'etat d'apres, on envoie celui d'avant.

    C'est la meme regle que dans la ligne d'une reponse : le doigt voit
    tout de suite le coeur se remplir, mais la couche donnee, elle, doit
    savoir d'ou l'on part pour basculer. Envoyer l'etat deja retourne
    faisait deux bascules pour un appui, et le like ne partait jamais.
  */
  async function basculerLaTete(
    courant: Comment,
    suivant: Comment,
    garder: (comment: Comment) => Promise<Comment>,
  ) {
    setTeteRepeinte(suivant);
    majCommentaire(suivant);
    try {
      const confirme = await garder(courant);
      setTeteRepeinte(confirme);
      majCommentaire(confirme);
    } catch {
      if (ouvert) {
        setTeteRepeinte(ouvert);
        majCommentaire(ouvert);
      }
    }
  }

  /** Petit message ephemere, le temps d'etre lu. */
  function flash(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), 2600);
  }

  function partagerLaReponse(commentaire: Comment) {
    setMenu(null);
    setPartage({ postId: commentaire.postId, texte: commentaire.content || undefined });
  }

  /*
    Supprimer une reponse n'emporte pas ce qui lui repond : ces reponses
    existent, leurs auteurs les ont ecrites. Elles restent atteignables
    par le fil du post, ou elles remontent en tete.
  */
  async function supprimerLaReponse(commentaire: Comment) {
    setMenu(null);
    setData((precedent) => precedent.filter((item) => item.id !== commentaire.id));
    try {
      await postsApi.removeComment(commentaire);
    } catch {
      reload();
      setEchec(t.fil.commun.suppressionImpossible);
    }
  }

  function majCommentaire(suivant: Comment) {
    setData((precedent) => precedent.map((item) => (item.id === suivant.id ? suivant : item)));
  }

  return (
    <SubScreen
      title={t.fil.commun.reponses}
      overlay={
        <>
          {/* La visionneuse est un calque : la fermer rend l'ecran tel
              qu'on l'avait laisse, au meme endroit du fil. */}
          <PhotoViewer src={vue} onClose={() => setVue(null)} />

          <Sheet open={menu !== null} anchor={ancreDuMenu} onClose={() => setMenu(null)}>
            {menu && (
              <>
                <SheetAction
                  icon={<PaperPlaneTilt className="sens-x" size={22} />}
                  onClick={() => partagerLaReponse(menu)}
                >
                  {t.fil.commun.partager}
                </SheetAction>

                {/* On ne signale pas ce qu'on a ecrit : on l'efface. */}
                {menu.author.id === user?.id ? (
                  <SheetAction
                    icon={<Trash size={22} />}
                    tone="danger"
                    onClick={() =>
                      ask(t.fil.commun.supprimerReponseQ, () => void supprimerLaReponse(menu))
                    }
                  >
                    {t.fil.commun.supprimerReponse}
                  </SheetAction>
                ) : (
                  <SheetAction
                    icon={<Flag size={22} />}
                    onClick={() =>
                      aller(reportPath('comment', menu.id, { postId: menu.postId }))
                    }
                  >
                    {t.fil.commun.signaler}
                  </SheetAction>
                )}
              </>
            )}
          </Sheet>

          <PartageSheet
            open={partage !== null}
            anchor={ancreDuMenu}
            onClose={() => setPartage(null)}
            quoi={partage ? { postId: partage.postId } : null}
            lien={partage ? lienDePost(partage.postId) : ''}
            texte={partage?.texte}
            onFait={flash}
          />

          <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />

          {toast && <p className="feed__toast">{toast}</p>}
        </>
      }
      /* Le champ repond toujours a la reponse ouverte : chacune a
         desormais son propre fil, il n'y a plus de cible a viser. */
      bottomBar={<CommentComposer poignee={composeur} onEnvoyer={envoyer} envoi={envoi} />}
    >
      {loading && !data && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}
      {echec && <p className="field__error">{echec}</p>}

      {/*
        La lignee, au-dessus et hors de vue a l'arrivee.

        On glisse pour la retrouver : c'est ce a quoi la reponse repond,
        et il ne devrait pas falloir revenir en arriere pour se le
        rappeler. Chaque maillon se lit en petit — ce n'est pas lui qu'on
        est venu lire — et mene a son propre fil d'un appui.
      */}
      <div ref={ligneeRef}>
        {post && (
          /*
            Une zone et non un lien, parce qu'elle contient un bouton.

            Un bouton dans un lien n'est pas du HTML valide, et les
            navigateurs n'en font pas tous la meme chose : la photo devait
            pouvoir s'ouvrir sans emporter la navigation du maillon. Meme
            facon de faire que la ligne d'une reponse, qui ouvre son fil
            tout en gardant ses propres boutons.
          */
          <div
            className="cm-lignee"
            role="button"
            tabIndex={0}
            onClick={() => aller(`/app/feed/${postId}/comments`, { state: { post } })}
            onKeyDown={(touche) => {
              if (touche.key !== 'Enter' && touche.key !== ' ') return;
              touche.preventDefault();
              aller(`/app/feed/${postId}/comments`, { state: { post } });
            }}
          >
            <span className="cm-lignee__colonne">
              <Avatar user={post.author} size={32} />
            </span>
            <span className="cm-lignee__corps">
              <span className="cm-lignee__head">
                <span className="cm-lignee__author">{post.author.username}</span>
                <span className="cm-item__time">· {timeAgo(post.createdAt)}</span>
              </span>
              {post.content && (
                <PostText
                  content={post.content}
                  mentions={post.mentions}
                  className="cm-lignee__content"
                />
              )}
              {/*
                La photo du post, dans son maillon.

                Le maillon ne portait que le texte : un post qui montre
                une photo se resumait a sa legende, et un post sans
                legende n'affichait rien du tout — un pseudo, une heure,
                et le vide. Or c'est souvent la photo qui est le sujet de
                tout ce qui suit.
              */}
              {post.imageUrl && (
                <button
                  type="button"
                  className="cm-lignee__photo"
                  aria-label={t.fil.commun.voirPhoto}
                  onClick={(clic) => {
                    /* La photo s'ouvre ici meme : sans cet arret, l'appui
                       emporterait aussi la navigation du maillon, et l'on
                       quitterait le fil qu'on etait venu lire. */
                    clic.stopPropagation();
                    setVue(post.imageUrl);
                  }}
                >
                  <img src={post.imageUrl} alt={t.fil.commun.photoDe(post.author.username)} />
                </button>
              )}
            </span>
          </div>
        )}

        {ancetres.map((ancetre) => (
          <Link
            key={ancetre.id}
            className="cm-lignee"
            to={`/app/feed/${postId}/comments/${ancetre.id}`}
            state={{ comment: ancetre, post }}
          >
            <span className="cm-lignee__colonne">
              <Avatar user={ancetre.author} size={32} />
            </span>
            <span className="cm-lignee__corps">
              <span className="cm-lignee__head">
                <span className="cm-lignee__author">{ancetre.author.username}</span>
                <span className="cm-item__time">· {timeAgo(ancetre.createdAt)}</span>
              </span>
              <PostText
                content={ancetre.content}
                mentions={ancetre.mentions}
                className="cm-lignee__content"
              />
            </span>
          </Link>
        ))}
      </div>

      {/*
        Tout ce qui commence a la reponse ouverte, dans un bloc qui fait
        au moins un ecran : sans cela, une reponse sans reponse ne peut
        pas monter en haut, faute de quoi defiler sous elle.
      */}
      <div className="cm-fil-bas" ref={repereDeLaTete}>
        {ouvert && (
          <article className="cm-tete">
            {/*
            L'auteur sur sa propre ligne, avatar compris.

            Le texte prend alors toute la largeur au lieu d'etre repousse
            dans une colonne : c'est le sujet de l'ecran, il se lit comme
            un post et non comme une reponse dans une liste.
          */}
            <Link className="cm-tete__auteur" to={profilePath(ouvert.author.id, user?.id)}>
              <Avatar user={ouvert.author} size={40} />
              <span className="cm-tete__pseudo">{ouvert.author.username}</span>
            </Link>

            <PostText
              content={ouvert.content}
              mentions={ouvert.mentions}
              className="cm-tete__content"
            />

            {/*
            L'heure et le jour, en clair.

            Partout ailleurs on donne l'age — « il y a 2 h » se lit sans
            calculer. Ici l'age ne suffit plus : c'est sous la reponse
            qu'on a ouverte qu'on veut savoir quand la chose a ete dite,
            et « il y a 3 j » ne repond pas a cette question.
          */}
            <p className="cm-tete__quand">{horodatage(ouvert.createdAt)}</p>

            {/*
            Ses propres actions, entre deux filets et etalees.

            Elle en avait dans la liste et les perdait en s'ouvrant : pour
            aimer la reponse qu'on vient d'ouvrir, il fallait revenir en
            arriere. Elles s'etalent ici plutot que de se serrer a gauche
            — c'est une rangee a elle seule, pas le pied d'une ligne.

            La bulle n'y est pas : on est deja dans son fil, et le champ
            du bas y repond.
          */}
            <footer className="cm-item__actions cm-tete__actions">
              <button
                className={`cm-item__action${ouvert.liked ? ' cm-item__action--liked' : ''}`}
                aria-label={ouvert.liked ? t.fil.commun.retirerLike : t.fil.commun.likerReponse}
                aria-pressed={ouvert.liked}
                onClick={() =>
                  void basculerLaTete(
                    ouvert,
                    {
                      ...ouvert,
                      liked: !ouvert.liked,
                      likeCount: Math.max(0, ouvert.likeCount + (ouvert.liked ? -1 : 1)),
                    },
                    postsApi.likeComment,
                  )
                }
              >
                <Heart size={16} weight={ouvert.liked ? 'fill' : 'regular'} />
                {ouvert.likeCount > 0 && <span>{ouvert.likeCount}</span>}
              </button>

              {/* Partager, entre aimer et ranger : c'est l'ordre d'un post
                ouvert, et le meme geste. */}
              <button
                className="cm-item__action"
                aria-label={t.fil.fil.partagerReponse}
                onClick={() => partagerLaReponse(ouvert)}
              >
                <PaperPlaneTilt className="sens-x" size={17} />
              </button>

              <button
                className={`cm-item__action${ouvert.bookmarked ? ' cm-item__action--saved' : ''}`}
                aria-label={
                  ouvert.bookmarked ? t.fil.commun.retirerSignet : t.fil.commun.enregistrer
                }
                aria-pressed={ouvert.bookmarked}
                onClick={() =>
                  void basculerLaTete(
                    ouvert,
                    { ...ouvert, bookmarked: !ouvert.bookmarked },
                    postsApi.bookmarkComment,
                  )
                }
              >
                <BookmarkSimple size={18} weight={ouvert.bookmarked ? 'fill' : 'regular'} />
                {ouvert.bookmarkCount > 0 && <span>{ouvert.bookmarkCount}</span>}
              </button>

            </footer>
          </article>
        )}

        {data && reponses.length === 0 && (
          <EmptyState title={t.fil.fil.vide} hint={t.fil.fil.videAide} />
        )}

        {reponses.length > 0 && (
          <>
            {/* Le mot qui dit ce qui suit : sans lui, la premiere reponse se
              collait a celle qu'on lit et paraissait en faire partie. */}
            <p className="cm-heading">{t.fil.commun.reponses}</p>
            <div className="cm-list">
              {reponses.map((commentaire) => (
                <CommentRow
                  key={commentaire.id}
                  comment={commentaire}
                  onChange={majCommentaire}
                  onMenu={(bouton) => {
                    ancreDuMenu.current = bouton;
                    setMenu(commentaire);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </SubScreen>
  );
}
